import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/** Tracks whether a token refresh is in progress to prevent multiple simultaneous refreshes */
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const isApiRequest = req.url.includes('/api/');
  const isAuthRoute =
    req.url.includes('/api/auth/login') ||
    req.url.includes('/api/auth/signup') ||
    req.url.includes('/api/auth/forgot') ||
    req.url.includes('/api/auth/reset-password') ||
    req.url.includes('/api/auth/me') ||
    req.url.includes('/api/auth/refresh');

  const cookieMatch = (typeof document !== 'undefined' ? document.cookie : '').match(
    /(?:^|;\s*)googtrans=([^;]+)/
  );
  let appLang = (localStorage.getItem('site_language') || 'en').toLowerCase();
  if (cookieMatch) {
    try {
      const v = decodeURIComponent(cookieMatch[1] || '').trim();
      if (v.endsWith('/hi')) appLang = 'hi';
      else if (v.endsWith('/en')) appLang = 'en';
    } catch {
      /* keep localStorage */
    }
  }

  const isGuidesContentApi = /\/api\/guides\b/i.test(req.url);
  const langHeader = isGuidesContentApi ? 'en' : appLang;

  // Build headers
  const headers: Record<string, string> = { 'X-App-Lang': langHeader };

  // CSRF: Read csrf_token cookie and attach to mutating requests (POST, PUT, DELETE, PATCH)
  const isMutatingRequest = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method.toUpperCase());
  if (isMutatingRequest) {
    const csrfToken = (typeof document !== 'undefined' ? document.cookie : '')
      .match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1];
    if (csrfToken) {
      headers['X-CSRF-Token'] = decodeURIComponent(csrfToken);
    }
  }

  const requestToSend = isApiRequest
    ? req.clone({
        withCredentials: true,
        setHeaders: headers
      })
    : req;

  return next(requestToSend).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!isApiRequest) return throwError(() => error);

      switch (error.status) {
        case 401:
          // Skip refresh for auth routes
          if (isAuthRoute) return throwError(() => error);

          // Try token refresh before logging out
          if (!isRefreshing) {
            isRefreshing = true;
            refreshSubject.next(false);

            return authService.refreshToken().pipe(
              switchMap((success) => {
                isRefreshing = false;
                refreshSubject.next(true);

                if (success) {
                  // Retry the original request
                  return next(requestToSend);
                } else {
                  authService.logout();
                  router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
                  return throwError(() => error);
                }
              }),
              catchError((refreshError) => {
                isRefreshing = false;
                refreshSubject.next(false);
                authService.logout();
                if (router.url !== '/login') {
                  router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
                }
                return throwError(() => error);
              })
            );
          } else {
            // Another request triggered while refresh is in progress — wait for it
            return refreshSubject.pipe(
              filter(ready => ready === true),
              take(1),
              switchMap(() => next(requestToSend))
            );
          }

        case 403:
          // Don't show generic "access denied" on auth routes — they handle their own errors
          if (!isAuthRoute) {
            toastService.error('Access denied. You do not have permission for this action.');
          }
          return throwError(() => error);

        case 429:
          const retryAfter = error.headers?.get('Retry-After');
          const waitMsg = retryAfter ? `Try again in ${retryAfter} seconds.` : 'Try again later.';
          toastService.error(`Too many requests. ${waitMsg}`);
          return throwError(() => error);

        case 423:
          toastService.error('Account temporarily locked. Please try again later or contact support.');
          return throwError(() => error);

        default:
          return throwError(() => error);
      }
    })
  );
};
